import { useVariant } from '@unleash/proxy-client-react';
import { useCallback, useEffect, useMemo, useRef } from 'react';
import {
  BROWSER_UNLOAD,
  LocalStorageKeys,
  PLAYING_SOURCE,
  PlayingSourceEnums,
  UrlType,
  videoSyncStatsEvent,
  VideoTypes,
} from 'src/constants/constants';
import { UNLEASH_FEATURE_FLAGS } from 'src/constants/type';
import eventTracker from 'src/lib/eventTracker/eventTracker';
import useSyncWatchStats from 'src/lib/hooks/periodic-sync/useSyncWatchStats';
import useWatchStatsRegisterSession from 'src/lib/hooks/periodic-sync/useWatchStatsRegisterSession';
import { videoUrlDetailsType } from 'src/lib/hooks/video-player/useVideoUrlDetails';
import { selectOttVideoDetails, selectPlayerParams, useSelector } from 'src/lib/redux';
import { isOTT } from 'src/lib/utilities';
import { LocalStorageInstance } from 'src/services/localStroage/LocalStorageService';
import { logger } from 'src/services/logger/loggerService';
import { periodicSyncStatsEncryption } from 'src/services/periodicSyncStatsEncryption';
import { watchStatsRegisterSessionPayload } from 'src/services/utils';
import { getQueryParams } from 'src/usePlayerParams';
import { v4 as uuidv4 } from 'uuid';

interface SyncStatsParams {
  eType: string;
  registerSessionPublicKey?: string;
  quality?: string;
}

type periodicSyncStats = {
  videoUrlDetails: videoUrlDetailsType;
  quality: string;
  qualityRef: React.RefObject<string | number | null>;
  isLowDataModeEnabled: React.RefObject<boolean>;
  isFetchedWatchStatsData: boolean;
  fetchWatchStatsData: any;
  batchId: string;
  manualExitPlayerRef: React.RefObject<boolean | null>;
};

export function usePeriodicSync({
  videoUrlDetails,
  qualityRef,
  quality,
  isFetchedWatchStatsData,
  isLowDataModeEnabled,
  fetchWatchStatsData,
  batchId,
  manualExitPlayerRef,
}: periodicSyncStats) {
  const sessionIdRef = useRef<string>(uuidv4());
  const registerSessionPublicKeyRef = useRef<string>();
  const syncIntervalRef = useRef<NodeJS.Timeout | null>(null);
  const periodicSyncCounter = useRef<number>(0);
  const previousExitAtRef = useRef<number>(0);
  const previousTimeRef = useRef<number>(new Date().getTime());
  const prevQualityRef = useRef<string | undefined>();
  const hasYouTubePlaybackBuffering = useRef(false);
  const hasYouTubePlaybackOnceBufferingRef = useRef(false);
  const isSessionRegistered = useRef(false);

  const { syncWatchStatsMutate } = useSyncWatchStats();
  const { watchStatsRegisterSessionMutate } = useWatchStatsRegisterSession();

  const searchParams = new URLSearchParams(window.location.search);
  const playingSource = searchParams.get(PLAYING_SOURCE);

  const isPeriodicSyncStatsEnabled =
    playingSource === PlayingSourceEnums.SKILLS
      ? useVariant(UNLEASH_FEATURE_FLAGS.VIDEO_SYNC_STATS_ROLLOUT_SKILLS)
      : { feature_enabled: true };

  // leave lecture friction
  const totalVideoWatchDuration = useRef<number>(0);

  const resetSessionWatchTracking = useCallback(() => {
    totalVideoWatchDuration.current = 0;
    previousTimeRef.current = Date.now();
    previousExitAtRef.current = 0;
    isSessionRegistered.current = false;
    sessionIdRef.current = uuidv4();
    registerSessionPublicKeyRef.current = undefined;
  }, []);

  const getSessionWatchSeconds = useCallback((): number => {
    const syncedSeconds = totalVideoWatchDuration.current;

    if (typeof window === 'undefined' || !window.player) {
      return syncedSeconds;
    }

    const player = window.player;
    if (player.paused?.() || player.seeking?.()) {
      return syncedSeconds;
    }

    const pendingSeconds = Math.floor((Date.now() - previousTimeRef.current) / 1000);
    return syncedSeconds + Math.min(Math.max(pendingSeconds, 0), 60);
  }, []);

  const getSessionId = useCallback((): string => {
    return sessionIdRef.current;
  }, []);

  const {
    typeId,
    videoId,
    entryPoint,
    isComplete,
    entityId,
    khazanaProgramId,
    contentId,
    scheduleId,
    type,
    vType,
  } = useSelector(selectPlayerParams);
  const isOTTMode = isOTT();
  const isPeriodicSyncStats = isPeriodicSyncStatsEnabled?.feature_enabled || isOTTMode;

  const ottVideoDetails = useSelector(selectOttVideoDetails);

  const ottTags = useMemo(() => {
    if (!ottVideoDetails) return [];

    // Configuration mapping: property name -> tag type
    const tagMappings: Array<{ property: keyof typeof ottVideoDetails; tagType: string }> = [
      { property: 'classes', tagType: 'class' },
      { property: 'exams', tagType: 'exam' },
      { property: 'chapters', tagType: 'chapter' },
      { property: 'subjects', tagType: 'subject' },
      { property: 'teachers', tagType: 'teacher' },
      { property: 'genre', tagType: 'genre' },
      { property: 'tags', tagType: 'default' },
    ];

    return tagMappings.flatMap(({ property, tagType }) => {
      const items = ottVideoDetails[property];
      if (!Array.isArray(items)) return [];
      return items
        .filter((item: any) => item?.id)
        .map((item: any) => ({ type: tagType, id: item.id }));
    });
  }, [ottVideoDetails]);

  /**
   * Clears the existing sync interval if it exists.
   * This prevents duplicate intervals and ensures clean reinitialization.
   */
  const clearSyncInterval = () => {
    if (syncIntervalRef.current) {
      clearInterval(syncIntervalRef.current);
      syncIntervalRef.current = null;
    }
  };

  /**
   * Starts a new sync interval that triggers `handleSyncStats`
   * with a `STREAM_SYNC` event every 60 seconds.
   */
  const startSyncInterval = () => {
    syncIntervalRef.current = setInterval(() => {
      handleSyncStats({ eType: videoSyncStatsEvent.STREAM_SYNC });
    }, 60000);
  };

  /**
   * Resets the sync interval by clearing the current interval (if any)
   * and starting a new one. Used to restart periodic sync behavior.
   */
  const resetSyncInterval = () => {
    clearSyncInterval();
    startSyncInterval();
  };

  /**
   * Registers a new video watch session with the backend.
   * Retrieves and stores a session public key used for stat encryption.
   */
  const handleRegisterVideoSession = async () => {
    if (!sessionIdRef.current || isSessionRegistered.current || vType === VideoTypes.ADMIN) return;

    isSessionRegistered.current = true;

    /**
     * Construct the payload for session registration.
     * Includes metadata like content ID, program ID, schedule, type, and more.
     */
    const resolvedTypeId = isOTTMode ? (ottVideoDetails?._id as string) : (typeId as string);

    // Get categoryId from query params for OTT
    const catId = isOTTMode ? searchParams.get('categoryId') : undefined;
    const widgetId = isOTTMode ? searchParams.get('widgetId') : undefined;
    const playlistId = isOTTMode ? searchParams.get('playlistId') : undefined;

    const metadata =
      isOTTMode && ottVideoDetails
        ? {
            ...(typeof ottVideoDetails.isTrailer === 'boolean' && {
              isTrailer: ottVideoDetails.isTrailer,
            }),
            ...(ottVideoDetails.language?.[0]?.name && {
              language: ottVideoDetails.language[0].name,
            }),
            widgetId: widgetId,
            playlistId: playlistId,
          }
        : '';

    const videoLength =
      isOTTMode && ottVideoDetails?.duration
        ? Math.floor(ottVideoDetails.duration)
        : Number.isFinite(window?.player?.duration?.())
          ? Math.floor(window.player.duration())
          : 0;

    const resolvedVideoId = isOTTMode ? (ottVideoDetails?.videoId as string) : (videoId as string);

    const payload = watchStatsRegisterSessionPayload(
      sessionIdRef.current,
      entryPoint,
      khazanaProgramId,
      contentId,
      scheduleId || '',
      videoUrlDetails,
      fetchWatchStatsData,
      resolvedTypeId,
      resolvedVideoId,
      playingSource as string,
      isComplete as boolean,
      entityId as string,
      batchId as string,
      vType as string,
      ottTags as any[],
      metadata,
      videoLength,
      catId as string,
    );

    try {
      // Send session registration request to backend
      await watchStatsRegisterSessionMutate(payload, {
        onSuccess: async (data: any) => {
          // Store the public key returned from server for encryption
          if (data?.data?.publicKey) {
            registerSessionPublicKeyRef.current = data.data.publicKey;
            previousTimeRef.current = Date.now(); // Start time tracking
          }

          // If video is paused when session starts, sync pause state immediately
          if (window?.player?.paused()) {
            handleSyncStats({
              eType: videoSyncStatsEvent.PAUSED,
            });
          }
        },
        onError: (error) => {
          console.error('Error in registerVideoSessionMutate:', error);
        },
      });
    } catch (error) {
      console.error('Error executing handleRegisterVideoSession:', error);
    }
  };

  /**
   * Handles syncing video watch statistics with the backend.
   * Encrypts and sends playback data such as duration, quality, and timestamps.
   *
   * @param {SyncStatsParams} param0 - Object containing the event type for sync.
   */
  const handleSyncStats = async ({ eType }: SyncStatsParams) => {
    const publicKey = registerSessionPublicKeyRef.current;
    if (!publicKey) return;

    const currentTime = Date.now(); // Current timestamp
    const previousTime = previousTimeRef.current; // Last sync timestamp
    previousTimeRef.current = currentTime; // Update for next sync

    /**
     * If the event is a STREAM_SYNC, cap duration at 60 seconds.
     * Otherwise, calculate actual time difference since handleSync call.
     */
    const timeDifference =
      eType === videoSyncStatsEvent.STREAM_SYNC
        ? 60
        : Math.floor((currentTime - previousTime) / 1000);

    const currentExitAt = Math.floor(window?.player?.currentTime?.() || 0); // Current playback position
    const playerDuration = Number.isFinite(window?.player?.duration?.())
      ? Math.floor(window.player.duration())
      : 0; // Total video duration

    /**
     * Payload to be encrypted and sent to the server.
     * Includes information about playback position, session ID, duration, and quality.
     */
    const payload = {
      id: sessionIdRef.current,
      eType: eType,
      seekAt: previousExitAtRef.current,
      exitAt: currentExitAt,
      start: currentTime,
      duration: Math.min(timeDifference, 60),
      quality: isLowDataModeEnabled.current === true ? 'ldm' : qualityRef.current,
      isAudio: false,
      len: playerDuration,
    };

    previousExitAtRef.current = currentExitAt; // Update for next sync

    try {
      // Encrypt the payload using the session public key
      const encryptedKey = await periodicSyncStatsEncryption(payload, publicKey);
      if (encryptedKey) {
        periodicSyncCounter.current += 1;
      }
      const syncVideoStatsPayload = { ...encryptedKey, id: sessionIdRef.current };

      logger.log(payload, 'payload');
      return new Promise((resolve, reject) => {
        syncWatchStatsMutate(syncVideoStatsPayload, {
          onSuccess: () => {
            totalVideoWatchDuration.current += Math.min(timeDifference, 60);
            resolve(eType);
          },
          onError: reject,
        });
      });
    } catch (err) {
      console.error('Sync stats failed:', err);
    }
  };

  useEffect(() => {
    resetSessionWatchTracking();
  }, [scheduleId, typeId, videoId, resetSessionWatchTracking]);

  useEffect(() => {
    if (
      (window?.player && isPeriodicSyncStats && isFetchedWatchStatsData) ||
      (khazanaProgramId && contentId && window?.player && isFetchedWatchStatsData)
    ) {
      handleRegisterVideoSession();
    }
  }, [isPeriodicSyncStats, window?.player, isFetchedWatchStatsData, scheduleId, typeId, videoId]);

  useEffect(() => {
    if (window?.player && playingSource && isPeriodicSyncStats) {
      handleRegisterVideoSession();
    }
  }, [playingSource, isPeriodicSyncStats, window?.player, scheduleId, typeId, videoId]);

  const handleExit = async (event: BeforeUnloadEvent, comingFrom?: string) => {
    if (event && !manualExitPlayerRef.current) {
      event.preventDefault();
      event.returnValue = '';
    }

    if (comingFrom === BROWSER_UNLOAD && manualExitPlayerRef?.current) {
      return;
    }

    const isPaused = window?.player?.paused();

    if (isPaused) {
      previousTimeRef.current = Date.now();
    }
    const result = await handleSyncStats({ eType: videoSyncStatsEvent.EXIT_VIDEO });

    if (isPaused) {
      clearSyncInterval();
    } else {
      resetSyncInterval();
    }
    eventTracker.pw_periodic_sync({
      session_id: sessionIdRef.current,
      counter: periodicSyncCounter.current,
      trigger: videoSyncStatsEvent.EXIT,
    });

    handleLearn2EarnParam();
    return result;
  };

  useEffect(() => {
    const player = window?.player;
    if (!player) return;

    /**
     * Handles video play events.
     * - Updates the previous play timestamp if not seeking.
     * - Resets the sync interval if the video is playing.
     *
     * Scenarios this addresses:
     * - Playback didn't work after changing video quality.
     * - Playback didn't work after seeking while paused.
     * - Playback didn't work after skipping forward/backward.
     */
    const handlePlay = () => {
      if (!player.seeking()) {
        previousTimeRef.current = Date.now();
      }
      if (!player.paused()) {
        resetSyncInterval();
      }
      if (type?.toLowerCase() === UrlType.YOUTUBE && !hasYouTubePlaybackOnceBufferingRef.current) {
        hasYouTubePlaybackOnceBufferingRef.current = true;
      }
    };

    /**
     * Handles video pause events.
     * - Clears sync interval.
     * - Triggers pause sync stats if not seeking.
     */
    const handlePause = () => {
      hasYouTubePlaybackBuffering.current = false;
      clearSyncInterval();
      if (!player.seeking()) {
        handleSyncStats({ eType: videoSyncStatsEvent.PAUSED });
      }
    };

    /**
     * Handles buffering state (e.g., during quality change or seeking).
     * - Triggers pause sync stats.
     * - Clears sync interval.
     */
    const handleBuffering = async () => {
      if (type?.toLowerCase() === UrlType.YOUTUBE && !hasYouTubePlaybackBuffering.current) {
        hasYouTubePlaybackBuffering.current = true;
        return;
      }
      if (!hasYouTubePlaybackOnceBufferingRef.current && type?.toLowerCase() === UrlType.YOUTUBE) {
        previousTimeRef.current = Date.now();
      }
      await handleSyncStats({ eType: videoSyncStatsEvent.PAUSED });
    };

    /**
     * Handles when a seek operation is completed.
     * - Triggers seek bar shift sync stats.
     * - Resets sync interval.
     */
    const handleSeeked = async () => {
      setTimeout(async () => {
        if (player.paused()) {
          clearSyncInterval();
          previousTimeRef.current = Date.now();
        }
        await handleSyncStats({ eType: videoSyncStatsEvent.SEEK_BAR_SHIFTED });
        if (!player.paused()) {
          resetSyncInterval();
        }
      }, 1000);
    };

    /**
     * Handles tab/browser exit.
     * - Triggers exit sync stats.
     * - Updates previous time if paused.
     * - Clears or resets interval based on pause state.
     */

    /**
     * Handles video end event for YouTube players.
     * - Clears sync interval.
     * - Triggers pause sync stats.
     */
    const handleYoutubeVideoEnded = () => {
      if (type?.toLowerCase() !== UrlType.YOUTUBE) return;
      clearSyncInterval();
      handleSyncStats({ eType: videoSyncStatsEvent.PAUSED });
    };
    const handleVideoEnded = (e: BeforeUnloadEvent) => {
      handleYoutubeVideoEnded();
      if (vType === PlayingSourceEnums.SKILLS) handleExit(e);
    };
    /**
     * Detects video quality change and triggers session break stats.
     * - Updates previous play timestamp if paused.
     */
    if (prevQualityRef.current && prevQualityRef.current !== quality) {
      if (player.paused()) {
        previousTimeRef.current = Date.now();
      }

      handleSyncStats({ eType: videoSyncStatsEvent.SESSION_BREAK });
    }

    // Store current quality for next comparison
    prevQualityRef.current = quality;

    // Register event listeners
    player.on('pause', handlePause);
    player.on('waiting', handleBuffering);
    player.on('seeked', handleSeeked);
    player.on('play', handlePlay);
    player.on('ended', handleVideoEnded);
    if (!manualExitPlayerRef.current && vType !== PlayingSourceEnums.SKILLS) {
      window.addEventListener('beforeunload', (e) => handleExit(e, BROWSER_UNLOAD));
    }

    // Cleanup on unmount
    return () => {
      player.off('pause', handlePause);
      player.off('waiting', handleBuffering);
      player.off('seeked', handleSeeked);
      player.off('play', handlePlay);
      player.off('ended', handleYoutubeVideoEnded);
      window.removeEventListener('beforeunload', handleExit);
      clearSyncInterval();
    };
  }, [window?.player, quality, type]);

  useEffect(() => {
    const shouldEnablePeriodicSync = (!!khazanaProgramId && !!contentId) || !!isPeriodicSyncStats;
    try {
      LocalStorageInstance.setItem(
        LocalStorageKeys.PERIODIC_SYNC_ENABLE,
        shouldEnablePeriodicSync ? 'true' : 'false',
      );
    } catch (error) {
      console.error('Failed to save periodicSyncEnable to localStorage:', error);
    }
  }, [isPeriodicSyncStats, khazanaProgramId, contentId]);

  return { handleSyncStats, handleExit, totalVideoWatchDuration, getSessionWatchSeconds, getSessionId };
}

const handleLearn2EarnParam = () => {
  try {
    const { learn2Earn } = getQueryParams();
    if (learn2Earn) {
      localStorage.setItem('videoLearn2EarnData', 'true');
    }
  } catch (error) {
    console.error('[Learn2Earn] Failed to handle URL param:', error);
  }
};
